import { useState } from "react";
import { MdArrowDownward, MdArrowUpward } from "react-icons/md";

import { getRankDisplay, getRankMove } from "MVVM/Views/Match/rank";
import type { TPlayerMatchStats } from "MVVM/Views/Match/@types";
import {
  PremierPlate,
  RankArrow,
  RankBadgeWrap,
  RankDelta,
  RankIcon,
  RankSwatch,
} from "MVVM/Views/Match/styledComponents";

// Only the rank fields, so a surface that has a rank but no scoreboard (the
// Top 100 page reads it off a clip tag) can show the same badge.
export type TRankBadgeStats = Pick<
  TPlayerMatchStats,
  "rank" | "rankType" | "rankOld" | "rankNew"
>;

// Lifted out of the scoreboard so the performance tab can show the same badge
// rather than building a second one that drifts from it.
export const RankBadge = ({
  stats,
  // The scoreboard and match history are where a rank change is news, because
  // they sit next to every other player's. Beside a single name on the duels
  // rows or the performance header it is one more number to read past.
  showChange = true,
}: {
  stats: TRankBadgeStats;
  showChange?: boolean;
}) => {
  const [iconFailed, setIconFailed] = useState(false);
  // rankNew is the post match value. rank is the in game snapshot, which for
  // the match you just played still reads as the rank you went in with.
  const display = getRankDisplay(stats.rankNew ?? stats.rank, stats.rankType);
  const move = showChange ? getRankMove(stats) : null;

  if (!display) return <>-</>;

  const title = move ? `${display.label} (${move.title})` : display.label;

  return (
    // The label is also read out, because a title attribute reaches neither a
    // screen reader reliably nor a touch user at all.
    <RankBadgeWrap title={title} aria-label={title}>
      {display.isPremier ? (
        <PremierPlate $color={display.color}>
          <i />
          <i />
          <span>
            <em>{display.label}</em>
          </span>
        </PremierPlate>
      ) : display.iconUrl && !iconFailed ? (
        <RankIcon
          src={display.iconUrl}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setIconFailed(true)}
        />
      ) : (
        <RankSwatch $color={display.color} />
      )}
      {move &&
        (move.delta === undefined ? (
          <RankArrow $promoted={move.promoted}>
            {move.promoted ? <MdArrowUpward /> : <MdArrowDownward />}
          </RankArrow>
        ) : (
          // Premier gets the swing, not an arrow: every match moves the rating,
          // so how far it moved is the only part worth reading.
          <RankDelta $promoted={move.promoted}>
            {move.delta > 0 ? "+" : "\u2212"}
            {Math.abs(move.delta)}
          </RankDelta>
        ))}
    </RankBadgeWrap>
  );
};
